<template>
  <loading-element v-if="isLoadingRecipes" />

  <div class="main bg-primary" v-else>
    <div class="rowSpaceBetween fullWidth mb-5">
      <TopDeck
        v-if="authStore.userInfos"
        firstElement="Mes"
        secondElement="menus"
        :coloredSecond="true"
      />
      <div class="rowCustom">
        <ThemeToggle />
        <AccountToggle />
      </div>
    </div>

    <div class="recapBanner pa-4 rounded-xl bg-secondary">
      <div class="recapRow">
        <div class="recapCol">
          <h3 class="recapTitle mb-2">Récapitulatif des contraintes de mes menus</h3>

          <p class="recapLine"><strong>Objectif :</strong> {{ $t(weightObjective) }}</p>
          <p class="recapLine">
            <strong>Calories cibles :</strong> {{ targetCalories.toFixed(0) }} kcal
            / jour
          </p>

          <div class="macros recapMacros mt-3">
            <p class="macroItem"><strong>Protéines :</strong> {{ proteinNeeds.toFixed(0) }} g</p>
            <p class="macroItem"><strong>Lipides :</strong> {{ fatNeeds.toFixed(0) }} g</p>
            <p class="macroItem"><strong>Glucides :</strong> {{ carbNeeds.toFixed(0) }} g</p>
          </div>
        </div>

        <div class="recapCol">
          <div class="cookingData">
            <p class="recapLine">
              <strong>Régime alimentaire :</strong>
              {{
                userConstraints.diet.length
                  ? userConstraints.diet.map((d) => $t(d)).join(", ")
                  : "Aucun"
              }}
            </p>
            <p class="recapLine">
              <strong>Intolérances alimentaires :</strong>
              {{
                userConstraints.intolerances.length
                  ? userConstraints.intolerances.map((i) => $t(i)).join(", ")
                  : "Aucune"
              }}
            </p>
            <p class="recapLine">
              <strong>Repas déjà pris dans la journée :</strong>
              {{
                userConstraints.takenMealsObjectives.length
                  ? userConstraints.takenMealsObjectives
                      .map((m) => $t(m))
                      .join(", ")
                  : "Aucun"
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- MENU SEMAINE -->
    <div v-if="displayedWeeklyData.daily && displayedWeeklyData.daily.length" class="mt-6">
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center flex-wrap" style="gap: 10px;">
          <h1 class="mr-3">Menu de la semaine</h1>
          <v-btn
            color="white"
            prepend-icon="mdi-printer"
            elevation="0"
            rounded="lg"
            size="small"
            @click="printMenu"
            :loading="isPrinting"
          >
            Imprimer
          </v-btn>
          <v-btn
            color="secondary"
            prepend-icon="mdi-refresh"
            elevation="0"
            rounded="lg"
            size="small"
            @click="rerollMenu"
          >
            Générer à nouveau
          </v-btn>
          <v-btn
            v-if="isSavingMenu"
            color="accent"
            elevation="0"
            rounded="lg"
            size="small"
            :loading="true"
          >
            Sauvegarde...
          </v-btn>
        </div>
        <div class="diverse-control">
          <v-tooltip location="bottom" max-width="260">
            <template #activator="{ props: tooltipProps }">
              <span v-bind="tooltipProps" class="diverse-label">
                🥗 Menus diversifiés
                <v-icon size="13" style="opacity:0.4;">mdi-information-outline</v-icon>
              </span>
            </template>
            Évite qu'une même recette apparaisse plusieurs fois dans la semaine. Chaque recette n'est proposée qu'une seule fois sur les 7 jours.
          </v-tooltip>
          <div class="diverse-pills">
            <button class="diverse-pill" :class="{ 'diverse-pill--active': diverse }" @click="diverse = true">Oui</button>
            <button class="diverse-pill" :class="{ 'diverse-pill--active': !diverse }" @click="diverse = false">Non</button>
          </div>
        </div>
      </div>

      <!-- Cuisiner moins -->
      <div class="sameMenu-row mb-4">
        <span class="sameMenu-label">🍳 Même plat midi & soir :</span>
        <div class="sameMenu-pills">
          <button
            v-for="(label, idx) in DAY_LABELS"
            :key="idx"
            class="sameMenu-pill"
            :class="{ 'sameMenu-pill--active': sameLunchDinnerDays.includes(idx) }"
            @click="sameLunchDinnerDays.includes(idx)
              ? sameLunchDinnerDays.splice(sameLunchDinnerDays.indexOf(idx), 1)
              : sameLunchDinnerDays.push(idx)"
          >
            {{ label }}
          </button>
        </div>
      </div>

      <v-alert
        v-if="showExpiredNotice"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        Votre menu sauvegardé a plus d'une semaine. Merci de générer et sauvegarder un nouveau menu.
      </v-alert>

      <div class="weekGrid" ref="menuSectionRef">
        <div
          v-for="(day, idx) in displayedWeeklyData.daily"
          :key="idx"
          class="dayCard bg-secondary pa-4 rounded-xl"
        >
          <div class="dayHeader d-flex justify-space-between align-center mb-3">
            <div class="d-flex align-center" style="gap: 8px;">
              <h2 class="dayTitle mb-0">Jour {{ idx + 1 }}</h2>
              <span v-if="sameLunchDinnerDays.includes(idx)" class="sameMenu-badge">Midi = Soir</span>
            </div>
            <p class="dayKcal mb-0">{{ day.totals.kcal.toFixed(0) }} kcal</p>
          </div>

          <div v-if="day.menu && Object.keys(day.menu).length">
            <div
              v-for="mealName in MEAL_ORDER.filter(m => day.menu[m] !== undefined)"
              :key="mealName"
              class="menuSection mb-3"
            >
              <h4 class="menuTitle mb-1 text-capitalize">{{ $t(mealName) }}</h4>
              <div v-if="day.menu[mealName] && day.menu[mealName].length" class="mealList d-flex flex-column">
                <v-card
                  v-for="(recipe, i2) in day.menu[mealName]"
                  :key="recipe.id || i2"
                  class="mealCard pa-3"
                  elevation="0"
                >
                  <div class="d-flex justify-space-between align-center">
                    <div>
                      <p class="mealName mb-1">{{ recipe.title || 'Sans titre' }}</p>
                      <div class="mealMeta">
                        <span class="mealKcal">{{ (recipe.totals?.kcal/recipe.servings ?? 0).toFixed(0) }} kcal</span>
                      </div>
                    </div>
                  </div>
                </v-card>
              </div>
              <p v-else class="text-caption text-grey">Aucune recette disponible pour ce repas.</p>
            </div>
          </div>

          <div class="dailyTotals mt-4">
            <p class="dailyTotals__label mb-1">Totaux journaliers</p>
            <p class="dailyTotals__values mb-0">
              <v-tooltip location="top">
                <template #activator="{ props: tp }">
                  <span v-bind="tp" :class="day.totals.protein >= proteinNeeds ? 'macro-ok' : 'macro-warn'">
                    P: {{ day.totals.protein.toFixed(0) }}g
                  </span>
                </template>
                Cible : {{ proteinNeeds.toFixed(0) }}g
              </v-tooltip>
              <span class="macro-sep"> • </span>
              <v-tooltip location="top">
                <template #activator="{ props: tp }">
                  <span v-bind="tp" :class="day.totals.fat <= fatNeeds + 5 ? 'macro-ok' : 'macro-warn'">
                    L: {{ day.totals.fat.toFixed(0) }}g
                  </span>
                </template>
                Cible : {{ fatNeeds.toFixed(0) }}g
              </v-tooltip>
              <span class="macro-sep"> • </span>
              <v-tooltip location="top">
                <template #activator="{ props: tp }">
                  <span v-bind="tp" :class="Math.abs(day.totals.carbs - carbNeeds) <= 20 ? 'macro-ok' : 'macro-warn'">
                    G: {{ day.totals.carbs.toFixed(0) }}g
                  </span>
                </template>
                Cible : {{ carbNeeds.toFixed(0) }}g
              </v-tooltip>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <v-snackbar
    v-if="errorMessage"
    v-model="errorMessage"
    color="error"
    top
    right
  >
    {{ errorMessage }}
  </v-snackbar>
  <v-snackbar v-model="menuSnack.show" color="success" timeout="2500" top right>
    {{ menuSnack.text }}
  </v-snackbar>
</template>

<script>
import { computed, ref, onMounted, watch, nextTick } from "vue";
import LoadingElement from "../Animations/LoadingElement.vue";
import TopDeck from "../Utils/TopDeck.vue";
import { useAuthStore } from "@/store/auth_store";
import {
  getBaseMetabolicRate,
  getDailyNeeds,
  getTargetCalories,
  calculateMacros,
} from "@/service/health_service";
import { getUserRecipes } from "@/service/recipe_service";
import { generateMenu, generateWeeklyMenu } from "@/service/menu_service";
import { getUserWeeklyMenu, saveUserWeeklyMenu } from "@/service/menu_storage_service";
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import AccountToggle from "@/components/Utils/AccountToggle.vue";
import html2canvas from "html2canvas";

export default {
  name: "MenuPage",
  components: { LoadingElement, TopDeck, ThemeToggle, AccountToggle },
  setup() {
    const authStore = useAuthStore();
    const errorMessage = ref("");
    const recipes = ref([]);
    const isLoadingRecipes = ref(false);
    const menuSectionRef = ref(null);
    const isPrinting = ref(false);
    const isSavingMenu = ref(false);
    const menuSnack = ref({ show: false, text: "" });
    const savedMenu = ref(null);
    const savedAt = ref(null);
    const menuSeed = ref(0);
    const useSavedMenu = ref(false);

    const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

    onMounted(() => {
      watch(
        () => authStore.userInfos,
        async () => {
          if (!authStore?.userid) return;
          try {
            isLoadingRecipes.value = true;
            const data = await getUserRecipes(authStore.userid);
            recipes.value = Object.entries(data).map(([id, value]) => ({
              id,
              ...value,
            }));
          } catch (error) {
            errorMessage.value =
              "Erreur lors du chargement des recettes utilisateur.";
            console.error(error);
          } finally {
            isLoadingRecipes.value = false;
          }

          try {
            const saved = await getUserWeeklyMenu(authStore.userid);
            savedMenu.value = saved?.weeklyMenu || null;
            savedAt.value = saved?.savedAt || null;
            const isFresh = saved?.savedAt && Date.now() - saved.savedAt < WEEK_MS;
            useSavedMenu.value = !!saved?.weeklyMenu && isFresh;
          } catch (error) {
            console.error("Erreur chargement menu sauvegardé:", error);
          }
        },
        { immediate: true }
      );
    });

    // Données du store
    const weight = computed(() => Number(authStore.userInfos?.actualWeight) || 70);
    const height = computed(() => Number(authStore.userInfos?.actualSize) || 175);
    const age = computed(() => authStore.userInfos?.age || 30);
    const sex = computed(() => authStore.userInfos?.sex || "male");
    const activityLevel = computed(() => authStore.userInfos?.activityLevel || "sedentary");
    const weightObjective = computed(() => authStore.userInfos?.weightObjective || "maintainWeight");
    const nap = computed(() => authStore.userInfos?.NAP || 1.5);

    //Cooking data
    const userConstraints = computed(() => ({
      targetCalories: targetCalories.value,
      takenMealsObjectives: authStore.userInfos?.takenMealsObjectives || [
        "breakfast",
        "lunch",
        "dinner",
      ],
      allergies: authStore.userInfos?.allergies || [],
      intolerances: authStore.userInfos?.intolerances || [],
      diet: authStore.userInfos?.diet || [],
      bannedIngredients: authStore.userInfos?.bannedIngredients || [],
      averageCookingTime:
        authStore.userInfos?.averageCookingTime || "15To30Minutes",
      cookingSkills: authStore.userInfos?.cookingSkills || null,
      equipement: authStore.userInfos?.equipement || [],
    }));

    const diverse = ref(true);
    const sameLunchDinnerDays = ref([]);

    const menuData = computed(() =>
      generateMenu(recipes.value, userConstraints.value)
    );

    const generatedWeeklyData = computed(() => {
      menuSeed.value;
      return generateWeeklyMenu(recipes.value, userConstraints.value, {
        diverse: diverse.value,
        sameLunchDinnerDays: sameLunchDinnerDays.value,
      });
    });

    const hasSavedMenu = computed(() => !!savedMenu.value);
    const isSavedFresh = computed(
      () => !!savedAt.value && Date.now() - savedAt.value < WEEK_MS
    );
    const showExpiredNotice = computed(
      () => hasSavedMenu.value && !isSavedFresh.value
    );
    const displayedWeeklyData = computed(() => {
      if (useSavedMenu.value && hasSavedMenu.value && isSavedFresh.value) {
        return savedMenu.value;
      }
      return generatedWeeklyData.value;
    });

    const showSaveButton = computed(() => !useSavedMenu.value);
    const saveButtonLabel = computed(() =>
      hasSavedMenu.value && isSavedFresh.value ? "Remplacer" : "Sauvegarder"
    );

    // Calculs nutritionnels
    const tmb = computed(() => getBaseMetabolicRate(age.value, height.value, weight.value, sex.value));
    const dailyNeeds = computed(() => getDailyNeeds(tmb.value, nap.value));
    const targetCalories = computed(() =>
      getTargetCalories(dailyNeeds.value, weightObjective.value)
    );

    const macros = computed(() =>
      calculateMacros(
        weight.value,
        activityLevel.value,
        weightObjective.value,
        targetCalories.value
      )
    );

    const proteinNeeds = computed(() => macros.value.proteinNeeds);
    const fatNeeds = computed(() => macros.value.fatNeeds);
    const carbNeeds = computed(() => macros.value.carbNeeds);

    const printMenu = async () => {
      if (!menuSectionRef.value) {
        console.error("Menu section ref non disponible");
        return;
      }

      isPrinting.value = true;

      try {
        const canvas = await html2canvas(menuSectionRef.value, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        });

        const imgData = canvas.toDataURL("image/png");
        const printWindow = window.open("", "_blank", "width=900,height=1200");
        
        printWindow.document.write(`
          <html>
            <head>
              <title>Menu de la semaine</title>
              <style>
                body { margin: 0; padding: 0; }
                img { max-width: 100%; height: auto; display: block; }
                @media print {
                  img { page-break-inside: avoid; }
                }
              </style>
            </head>
            <body>
              <img src="${imgData}" />
            </body>
          </html>
        `);

        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        };
      } catch (error) {
        console.error("Erreur lors de l'impression:", error);
        errorMessage.value = "Erreur lors de la génération de l'impression";
      } finally {
        isPrinting.value = false;
      }
    };

    const saveMenu = async () => {
      if (!authStore.userid) {
        errorMessage.value = "Utilisateur non connecté.";
        return;
      }
      const menuToSave = generatedWeeklyData.value;
      if (!menuToSave?.daily?.length) {
        errorMessage.value = "Aucun menu à sauvegarder.";
        return;
      }

      isSavingMenu.value = true;
      try {
        const meta = { diverse: diverse.value };
        const res = await saveUserWeeklyMenu(authStore.userid, menuToSave, meta);
        savedMenu.value = menuToSave;
        savedAt.value = res?.savedAt || Date.now();
        useSavedMenu.value = true;
        menuSnack.value = { show: true, text: "Menu sauvegardé ✅" };
      } catch (error) {
        console.error("Erreur sauvegarde menu:", error);
        errorMessage.value = "Erreur lors de la sauvegarde du menu.";
      } finally {
        isSavingMenu.value = false;
      }
    };

    const rerollMenu = async () => {
      useSavedMenu.value = false;
      menuSeed.value += 1;
      await nextTick();
      await saveMenu();
    };

    return {
      //User data
      authStore,
      errorMessage,
      recipes,
      isLoadingRecipes,
      weightObjective,
      targetCalories,
      proteinNeeds,
      fatNeeds,
      carbNeeds,
      //Cooking data,
      userConstraints,
      menuData,
      generatedWeeklyData,
      displayedWeeklyData,
      showSaveButton,
      saveButtonLabel,
      showExpiredNotice,
      diverse,
      //Print
      menuSectionRef,
      isPrinting,
      printMenu,
      rerollMenu,
      saveMenu,
      isSavingMenu,
      menuSnack,
      MEAL_ORDER: ['breakfast', 'lunch', 'snack', 'dinner'],
      sameLunchDinnerDays,
      DAY_LABELS: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
    };
  },
};
</script>

<style scoped>
.recapBanner {
  position: relative;
  border-radius: 18px;
  padding: 1.5rem;
  background: rgba(var(--v-theme-secondary), 0.92);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.recapBanner::after {
  content: none;
}

.macros p {
  margin: 4px 0;
}

.recapTitle {
  margin: 0 0 0.5rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
}

.recapLine {
  margin: 0 0 0.4rem;
  font-size: 0.95rem;
  color: rgba(var(--v-theme-text), 0.8);
}

.recapLine strong {
  color: rgb(var(--v-theme-text));
  font-weight: 600;
}

.recapMacros {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.6rem;
}

.macroItem {
  margin: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(var(--v-theme-text), 0.06);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  font-size: 0.88rem;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}

.menuSection {
  border-bottom: 1px solid rgba(var(--v-theme-text), 0.08);
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}

.menuTitle {
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: 0.01em;
  color: rgb(var(--v-theme-accent));
  margin-bottom: 0.6rem;
}

.menuSection:last-child {
  border-bottom: none;
}

.dayCard {
  position: relative;
  overflow: hidden;
  min-height: 120px;
  background: rgba(var(--v-theme-secondary), 0.9);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.18);
}

.dayCard::after {
  content: none;
}

.dayHeader {
  position: relative;
  z-index: 1;
}

.dayTitle {
  font-size: 1.15rem;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
}

.dayKcal {
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: rgba(var(--v-theme-text), 0.06);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  color: rgb(var(--v-theme-text));
}

.mealList {
  gap: 0.75rem;
}

.mealCard {
  position: relative;
  border-radius: 18px;
  background: rgba(var(--v-theme-tertiary), 0.9);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  box-shadow: 0 12px 22px rgba(0, 0, 0, 0.16);
}

.mealName {
  font-weight: 600;
  font-size: 1rem;
  color: rgb(var(--v-theme-text));
}

.mealMeta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: rgba(var(--v-theme-text), 0.72);
}

.mealCategory {
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(var(--v-theme-text), 0.06);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  text-transform: capitalize;
}

.mealKcal {
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(var(--v-theme-accent), 0.18);
  color: rgb(var(--v-theme-accent));
  font-weight: 600;
}

.macro-ok {
  color: #4caf50;
  font-weight: 600;
}

.macro-warn {
  color: #ff9800;
  font-weight: 600;
}

.macro-sep {
  color: rgba(var(--v-theme-text), 0.3);
}

.dailyTotals {
  position: relative;
  z-index: 1;
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(var(--v-theme-text), 0.1);
}

.dailyTotals__label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 700;
  color: rgba(var(--v-theme-text), 0.55);
}

.dailyTotals__values {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}

.rowCustom {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.recapRow {
  display: flex;
  gap: 1.5rem;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
}

.recapCol {
  flex: 1 1 320px; /* grow, shrink, base width -> allows wrapping */
  min-width: 240px;
  background: rgba(var(--v-theme-tertiary), 0.85);
  padding: 1.1rem 1.2rem;
  border-radius: 16px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.16);
  position: relative;
  z-index: 1;
}

.cookingData .recapLine:last-child {
  margin-bottom: 0;
}

.diverse-control {
  display: flex;
  align-items: center;
  gap: 10px;
}

.diverse-label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.7);
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  cursor: default;
}

.diverse-pills {
  display: flex;
  gap: 4px;
}

.diverse-pill {
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid rgba(var(--v-theme-text), 0.18);
  color: rgba(var(--v-theme-text), 0.6);
  background: transparent;
  font-family: inherit;
  transition: all 0.15s;
}

.diverse-pill--active {
  background: rgb(var(--v-theme-accent));
  color: #fff;
  border-color: rgb(var(--v-theme-accent));
}

.sameMenu-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.sameMenu-label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.7);
  white-space: nowrap;
}

.sameMenu-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.sameMenu-pill {
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid rgba(var(--v-theme-text), 0.18);
  color: rgba(var(--v-theme-text), 0.6);
  background: transparent;
  font-family: inherit;
  transition: all 0.15s;
}

.sameMenu-pill:hover:not(.sameMenu-pill--active) {
  background: rgba(var(--v-theme-text), 0.05);
}

.sameMenu-pill--active {
  background: rgb(var(--v-theme-accent));
  color: #fff;
  border-color: rgb(var(--v-theme-accent));
}

.sameMenu-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(var(--v-theme-accent), 0.15);
  color: rgb(var(--v-theme-accent));
  border: 1px solid rgba(var(--v-theme-accent), 0.25);
}

.weekGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}
</style>
